import { forwardRef, useCallback, useEffect, useState } from "react";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import TreeItem, {
  TreeItemProps,
  useTreeItem,
  TreeItemContentProps,
} from "@mui/lab/TreeItem";
import {
  ExpandMore,
  ChevronRight,
  Edit,
  Delete,
  FileCopy,
  CheckBox,
  CheckBoxOutlineBlank,
} from "@mui/icons-material";
import { Box } from "@mui/system";
import { TreeView } from "@mui/lab";
import { useNavigate } from "react-router-dom";

import { Modal } from "../../shared/components/Modal";
import {
  EstablishmentService,
  IUnityEstablishment,
} from "../../shared/services/api/EstablishmentService";
import { changeTitle, confirmation, feedback } from "../../shared/events";

export const ListagemDeEstabelecimentos = () => {
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [replicaBase, setReplicaBase] = useState("");
  const [establishments, setEstablishments] = useState<IUnityEstablishment[]>(
    []
  );
  const [establishmentSelected, setEstablishmentSelected] = useState<string[]>(
    []
  );
  const [establishmentExpanded, setEstablishmentExpanded] = useState<string[]>(
    []
  );

  const navigate = useNavigate();

  const handleToggleTreeEstablishment = (
    _: React.SyntheticEvent,
    nodeIds: string[]
  ) => {
    setEstablishmentExpanded(nodeIds);

    setEstablishmentSelected((state) => {
      if (state.includes(nodeIds[0]))
        return state.filter((item) => item !== nodeIds[0]);
      return state
        .filter((item) => !establishmentExpanded.includes(item) && item)
        .concat(nodeIds);
    });
  };

  const handleSelectTreeEstablishment = (
    _: React.SyntheticEvent,
    nodeIds: string[]
  ) => {
    setEstablishmentSelected((state) => {
      if (state.includes(nodeIds[0]))
        return state.filter((item) => item !== nodeIds[0]);
      return state
        .filter((item) => !establishmentExpanded.includes(item) && item)
        .concat(nodeIds);
    });
  };

  const CustomContent = forwardRef(function CustomContent(
    props: TreeItemContentProps,
    ref
  ) {
    const {
      classes,
      className,
      label,
      nodeId,
      icon: iconProp,
      expansionIcon,
      displayIcon,
    } = props;

    const {
      disabled,
      expanded,
      selected,
      focused,
      handleExpansion,
      handleSelection,
      preventSelection,
    } = useTreeItem(nodeId);

    const icon = iconProp || expansionIcon || displayIcon;

    const handleMouseDown = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      preventSelection(event);
    };

    const handleExpansionClick = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      handleExpansion(event);
    };

    const handleSelectionClick = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      handleSelection(event);
    };

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className={clsx(className, classes.root, {
          [classes.expanded]: expanded,
          [classes.selected]: selected,
          [classes.focused]: focused,
          [classes.disabled]: disabled,
        })}
        onMouseDown={handleMouseDown}
        ref={ref as React.Ref<HTMLDivElement>}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div onClick={handleExpansionClick} className={classes.iconContainer}>
          {icon}
        </div>
        <Typography
          onClick={handleSelectionClick}
          component="div"
          className={classes.label}
        >
          {label}
        </Typography>
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            color="primary"
            sx={{ mr: 0.5 }}
            disabled={nodeId.split("_")[1] === "null"}
            onClick={() =>
              confirmation(
                "Você tem certeza que deseja excluir essa configuração?",
                "delete",
                () => handleDeleteConfig(nodeId.split("_")[1]),
                () => setEstablishmentSelected([])
              )
            }
          >
            <Delete fontSize="medium" />
          </IconButton>
          <IconButton
            size="small"
            sx={{ mr: 0.5 }}
            onClick={() => navigate(`/estabelecimentos/configuracao/${nodeId}`)}
          >
            <Edit color="primary" fontSize="medium" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            disabled={nodeId.split("_")[1] === "null"}
            onClick={() => (
              setModalOpen(true), setReplicaBase(nodeId.split("_")[1])
            )}
          >
            <FileCopy fontSize="medium" />
          </IconButton>
        </Box>
      </div>
    );
  });

  const CustomTreeItem = (props: TreeItemProps) => (
    <TreeItem {...props} ContentComponent={CustomContent} />
  );

  const getTreeEstablishments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await EstablishmentService.getTreeEstablishments();

      if (response) {
        setEstablishments(response);
      }
    } catch (error) {
      console.warn(error);
      // feedback(errorResponse(error), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const renderCustomTreeItem = useCallback(
    (establishment: IUnityEstablishment) => {
      return (
        <CustomTreeItem
          key={establishment.idEstabelecimento}
          nodeId={`${establishment.idEstabelecimento}_${establishment.idConfiguracao}`}
          label={`${establishment.idEstabelecimento} - ${establishment.nomeEstabelecimento}`}
        >
          {establishment.estabelecimentos.length > 0
            ? establishment.estabelecimentos.map((item) =>
                renderCustomTreeItem(item)
              )
            : null}
        </CustomTreeItem>
      );
    },
    []
  );

  const handleCancel = useCallback(() => {
    setModalOpen(false);
    setReplicaBase("");
    setEstablishmentSelected([]);
    setEstablishmentExpanded([]);
  }, []);

  const handleDeleteConfig = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await EstablishmentService.deleteConfig(id);

        if (response) {
          getTreeEstablishments();
          setEstablishmentSelected([]);
          feedback("Configuração excluída com sucesso!", "success");
        } else {
          feedback("Ocorreu um erro...", "error");
        }
      } catch (error) {
        feedback(error.message, "error");
      } finally {
        setLoading(false);
      }
    },
    [replicaBase, establishmentSelected]
  );

  const handleReplicateConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await EstablishmentService.postReplicateConfigs(
        replicaBase,
        establishmentSelected
      );

      if (response) {
        setModalOpen(false);
        getTreeEstablishments();
        setEstablishmentSelected([]);
        setEstablishmentExpanded([]);
        feedback("Configuração replicada com sucesso!", "success");
      } else {
        feedback("Ocorreu um erro...", "error");
      }
    } catch (error) {
      feedback(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [replicaBase, establishmentSelected]);

  const handleValidadeReplicate = useCallback(() => {
    var canReplicate = true;

    establishmentSelected.map((sel) => {
      establishments.map((iN1) => {
        if (sel === iN1.idEstabelecimento && !!iN1.idConfiguracao) {
          canReplicate = false;
        }

        if (iN1.estabelecimentos.length) {
          iN1.estabelecimentos.map((iN2) => {
            if (sel === iN2.idEstabelecimento && !!iN2.idConfiguracao) {
              canReplicate = false;
            }

            if (iN2.estabelecimentos.length) {
              iN2.estabelecimentos.map((iN3) => {
                if (sel === iN3.idEstabelecimento && !!iN3.idConfiguracao) {
                  canReplicate = false;
                }
              });
            }
          });
        }
      });
    });

    if (canReplicate) {
      handleReplicateConfig();
    } else {
      confirmation(
        `Um ou mais estabelecimentos selecionados já possui configuração.     
           Ao confirmar, as configurações serão substituídas.`,
        "confirmation",
        () => handleReplicateConfig(),
        () => {}
      );
    }
  }, [establishmentSelected, establishments]);

  useEffect(() => {
    getTreeEstablishments();
    changeTitle("Lista de Estabelecimentos");

    return () => changeTitle("");
  }, []);

  return (
    <>
      <Grid container>
        <TableContainer component={Paper}>
          <Table>
            <TableRow>
              <TableCell>Estabelecimentos</TableCell>
            </TableRow>
            <TableBody>
              <TableRow>
                <TreeView
                  aria-label="file system navigator"
                  defaultCollapseIcon={<ExpandMore />}
                  defaultExpandIcon={<ChevronRight />}
                >
                  {loading ? (
                    "Carregando..."
                  ) : establishments.length > 0 ? (
                    establishments.map((item) => renderCustomTreeItem(item))
                  ) : (
                    <Box padding={2}>
                      <Typography color="secondary">
                        Nenhum registro encontrado...
                      </Typography>
                    </Box>
                  )}
                </TreeView>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Modal
        loading={loading}
        opened={modalOpen}
        onClose={handleCancel}
        onClick={handleValidadeReplicate}
        disabled={!establishmentSelected.length}
        labelSaveButton="Replicar"
        labelCloseButton="Cancelar"
        title="Replicar Configurações para outros estabelecimentos"
      >
        <Grid item xs={12} sm={12}>
          <Box
            marginTop={3}
            marginBottom={0.3}
            sx={{ border: `1px solid gray`, borderRadius: 4 }}
          >
            <Box sx={{ borderBottom: `1px solid gray` }} padding={2}>
              <Typography>Estabelecimentos</Typography>
            </Box>
            <Box padding={2} style={{ overflow: "auto" }}>
              <TreeView
                multiSelect
                defaultCollapseIcon={<ExpandMore />}
                defaultExpandIcon={<ChevronRight />}
                expanded={establishmentExpanded}
                selected={establishmentSelected}
                onNodeToggle={handleToggleTreeEstablishment}
                onNodeSelect={handleSelectTreeEstablishment}
              >
                {establishments?.map((itemN1) => (
                  <TreeItem
                    key={itemN1.idEstabelecimento}
                    nodeId={itemN1.idEstabelecimento}
                    label={
                      <Box
                        // className="clicktest"
                        sx={{ display: "flex", alignItems: "center", my: 0.7 }}
                      >
                        {establishmentSelected.includes(
                          itemN1.idEstabelecimento
                        ) ? (
                          <CheckBox />
                        ) : (
                          <CheckBoxOutlineBlank />
                        )}
                        <Box
                          paddingLeft={1}
                        >{`${itemN1.idEstabelecimento} - ${itemN1.nomeEstabelecimento}`}</Box>
                      </Box>
                    }
                  >
                    {itemN1.estabelecimentos?.map((itemN2) => (
                      <TreeItem
                        key={itemN2.idEstabelecimento}
                        nodeId={itemN2.idEstabelecimento}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              my: 0.7,
                              pl: 1,
                            }}
                          >
                            {establishmentSelected.includes(
                              itemN2.idEstabelecimento
                            ) ? (
                              <CheckBox />
                            ) : (
                              <CheckBoxOutlineBlank />
                            )}
                            <Box
                              paddingLeft={1}
                            >{`${itemN2.idEstabelecimento} - ${itemN2.nomeEstabelecimento}`}</Box>
                          </Box>
                        }
                      >
                        {itemN2?.estabelecimentos?.map((itemN3) => (
                          <TreeItem
                            key={itemN3.idEstabelecimento}
                            nodeId={itemN3.idEstabelecimento}
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  my: 0.7,
                                  pl: 1,
                                }}
                              >
                                {establishmentSelected.includes(
                                  itemN3.idEstabelecimento
                                ) ? (
                                  <CheckBox />
                                ) : (
                                  <CheckBoxOutlineBlank />
                                )}
                                <Box paddingLeft={1}>
                                  {`${itemN3.idEstabelecimento} - ${itemN3.nomeEstabelecimento}`}
                                </Box>
                              </Box>
                            }
                          />
                        ))}
                      </TreeItem>
                    ))}
                  </TreeItem>
                ))}
              </TreeView>
            </Box>
          </Box>
        </Grid>
      </Modal>
    </>
  );
};
